import { useEffect, useRef, useState } from "react";
import SharedSentenceNarratorPlayer from "./SharedSentenceNarratorPlayer";

const FOCUSABLE_SELECTOR = [
  "a[href]",
  "button:not([disabled])",
  "input:not([disabled])",
  "select:not([disabled])",
  "textarea:not([disabled])",
  "summary",
  '[tabindex]:not([tabindex="-1"])',
].join(",");

function DeferredExample({ children }) {
  const anchorRef = useRef(null);
  const [shouldMount, setShouldMount] = useState(false);

  useEffect(() => {
    if (shouldMount) return undefined;

    const anchor = anchorRef.current;
    if (!anchor || typeof IntersectionObserver === "undefined") {
      setShouldMount(true);
      return undefined;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry?.isIntersecting) return;
        setShouldMount(true);
        observer.disconnect();
      },
      { rootMargin: "360px 0px" }
    );

    observer.observe(anchor);
    return () => observer.disconnect();
  }, [shouldMount]);

  return (
    <div ref={anchorRef}>
      {shouldMount ? (
        children
      ) : (
        <div
          className="flex min-h-24 items-center justify-center rounded-2xl border border-slate-200 bg-slate-50 px-4 text-sm text-slate-500"
          aria-hidden="true"
        >
          The narrator example loads as you scroll.
        </div>
      )}
    </div>
  );
}

export default function IntonationHelpModal({ open, onClose }) {
  const dialogRef = useRef(null);
  const closeButtonRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;

    const previouslyFocused = document.activeElement;

    const getFocusableElements = () => {
      const dialog = dialogRef.current;
      if (!dialog) return [];

      return Array.from(dialog.querySelectorAll(FOCUSABLE_SELECTOR)).filter(
        (element) =>
          element instanceof HTMLElement &&
          !element.hasAttribute("disabled") &&
          element.getAttribute("aria-hidden") !== "true" &&
          (element.offsetWidth > 0 || element.offsetHeight > 0)
      );
    };

    const onKeyDown = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        onClose();
        return;
      }

      if (event.key !== "Tab") return;

      const dialog = dialogRef.current;
      const focusable = getFocusableElements();

      if (!dialog || focusable.length === 0) {
        event.preventDefault();
        dialog?.focus();
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];
      const activeElement = document.activeElement;
      const focusIsOutside = !dialog.contains(activeElement);

      if (event.shiftKey && (activeElement === first || focusIsOutside)) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && (activeElement === last || focusIsOutside)) {
        event.preventDefault();
        first.focus();
      }
    };

    document.addEventListener("keydown", onKeyDown, true);

    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";

    const focusFrame = window.requestAnimationFrame(() => {
      closeButtonRef.current?.focus();
    });

    return () => {
      window.cancelAnimationFrame(focusFrame);
      document.removeEventListener("keydown", onKeyDown, true);
      document.body.style.overflow = previousOverflow;

      if (previouslyFocused instanceof HTMLElement) {
        previouslyFocused.focus();
      }
    };
  }, [open, onClose]);

  if (!open) return null;

  const englishExampleHref =
    "/subject/6981ebd4124656975de04a26/14/9";
  const norwegianExampleHref =
    "/subject/68614e9d7297f8ddb1d8d2c3/0/3";

  const voicingPattern = [
    ["Gu", "mostly voiced", true],
    ["tt", "voiceless", false],
    ["en", "voiced", true],
    ["sk", "voiceless", false],
    ["-river", "mostly voiced", true],
  ];

  return (
    <div className="fixed inset-0 z-[250]">
      <button
        type="button"
        tabIndex={-1}
        aria-label="Close help"
        onClick={onClose}
        className="absolute inset-0 bg-black/40"
      />

      <div className="absolute inset-0 p-0 sm:p-4">
        <div
          ref={dialogRef}
          tabIndex={-1}
          role="dialog"
          aria-modal="true"
          aria-labelledby="intonation-help-title"
          aria-describedby="intonation-help-subtitle"
          className="relative flex h-full w-full flex-col overflow-hidden bg-white outline-none sm:rounded-2xl sm:border sm:border-gray-200 sm:shadow-2xl"
          onClick={(event) => event.stopPropagation()}
        >
          <header className="sticky top-0 z-20 shrink-0 border-b border-gray-200 bg-white/95 backdrop-blur">
            <div className="flex items-start justify-between gap-3 px-4 py-3 sm:px-6">
              <div className="min-w-0">
                <h2
                  id="intonation-help-title"
                  className="text-xl font-semibold text-gray-900 sm:text-2xl"
                >
                  How to read the graph
                </h2>
                <p
                  id="intonation-help-subtitle"
                  className="mt-0.5 max-w-2xl text-sm leading-5 text-gray-600 sm:text-base sm:leading-6"
                >
                  Pitch track, loudness, and voicing
                </p>
              </div>

              <button
                ref={closeButtonRef}
                type="button"
                onClick={onClose}
                className="inline-flex shrink-0 items-center gap-2 rounded-xl border border-gray-200 bg-white px-3.5 py-2.5 text-base font-semibold text-gray-800 shadow-sm transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
              >
                <span className="text-lg leading-none" aria-hidden="true">
                  ✕
                </span>
                <span className="hidden sm:inline">Close</span>
              </button>
            </div>
          </header>

          <main className="min-h-0 flex-1 overflow-y-auto overscroll-contain">
            <div className="mx-auto w-full max-w-6xl space-y-8 px-4 py-6 sm:px-6 lg:px-8">
              <section className="space-y-4" aria-labelledby="pitch-movement-heading">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-full border border-blue-200 bg-blue-50 px-3 py-1.5 text-sm font-semibold text-blue-700">
                      English example
                    </div>

                    <h3
                      id="pitch-movement-heading"
                      className="text-xl font-semibold text-gray-900 sm:text-2xl"
                    >
                      1. Follow the phrase-level rise
                    </h3>
                  </div>

                  <p className="mt-2 max-w-5xl text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
                    Listen at <span className="font-semibold">100%</span>. Then replay
                    at <span className="font-semibold">50%</span> and follow the blue
                    line from <span className="font-semibold">“Wouldn’t”</span> to{" "}
                    <span className="font-semibold">“much.”</span>
                  </p>
                </div>

                <SharedSentenceNarratorPlayer
                  href={englishExampleHref}
                  label="English narrator"
                  compact
                />

                <div className="rounded-xl border border-blue-100 bg-blue-50/50 p-4 text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
                  <p>
                    In <span className="font-semibold">“Wouldn’t it be much warmer there?”</span>,
                    the first rise begins in <span className="font-semibold">“Wouldn’t”</span>,
                    continues through the unstressed{" "}
                    <span className="font-semibold">“it be”</span>, and approaches a
                    high point around <span className="font-semibold">“much.”</span> It
                    is one phrase-level movement—not a new rise on every word.
                  </p>
                  <div className="mt-3 rounded-xl border border-blue-200 bg-white px-4 py-3">
                    <div className="font-semibold text-gray-900">
                      Pitch and intensity can move independently
                    </div>
                    <p className="mt-1 text-sm leading-6 text-gray-700 sm:text-base">
                      From <span className="font-semibold">“Wouldn’t”</span> through{" "}
                      <span className="font-semibold">“much,”</span> the blue pitch line
                      rises while the grey intensity line stays broadly stable. The
                      speaker raises the pitch of the voice without progressively
                      becoming louder. A rising intonation does not necessarily mean an
                      increase in volume.
                    </p>
                  </div>
                  <p className="mt-2 text-sm leading-6 text-gray-600 sm:text-base">
                    Brief blue gaps can occur inside the same movement when a sound is
                    voiceless.
                  </p>
                </div>
              </section>

              <section className="space-y-4" aria-labelledby="pitch-gaps-heading">
                <div>
                  <div className="flex flex-wrap items-center gap-2">
                    <div className="inline-flex rounded-full border border-indigo-200 bg-indigo-50 px-3 py-1.5 text-sm font-semibold text-indigo-700">
                      Norwegian example
                    </div>

                    <h3
                      id="pitch-gaps-heading"
                      className="text-xl font-semibold text-gray-900 sm:text-2xl"
                    >
                      2. Read the gaps
                    </h3>
                  </div>

                  <p className="mt-2 max-w-5xl text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
                    Voiced sounds use vibrating vocal folds (vocal cords); voiceless
                    sounds do not. The blue line appears when that vibration is regular
                    enough to measure pitch. It may disappear during voiceless sounds or
                    pauses, while the grey intensity line can continue because sound is
                    still present.
                  </p>
                </div>

                <DeferredExample>
                  <SharedSentenceNarratorPlayer
                    href={norwegianExampleHref}
                    label="Norwegian narrator"
                    compact
                  />
                </DeferredExample>

                <div className="rounded-2xl border border-blue-200 bg-blue-50/40 p-4 sm:p-5">
                  <div className="text-lg font-semibold text-gray-900 sm:text-xl">
                    Simplified voicing map
                  </div>

                  <div className="mt-3 grid grid-cols-5 overflow-hidden rounded-xl border border-gray-200 bg-white text-center">
                    {voicingPattern.map(([sound, label, voiced], index) => (
                      <div
                        key={sound}
                        className={index < 4 ? "border-r border-gray-200" : ""}
                      >
                        <div
                          className={[
                            "px-1.5 py-2.5 text-sm font-semibold sm:px-3 sm:text-base",
                            voiced ? "text-blue-700" : "text-slate-700",
                          ].join(" ")}
                        >
                          {sound}
                        </div>
                        <div
                          className={[
                            "border-t border-gray-200 px-1 py-2.5 text-xs font-medium leading-tight sm:px-2 sm:text-sm",
                            voiced
                              ? "bg-blue-50 text-blue-700"
                              : "bg-slate-50 text-slate-700",
                          ].join(" ")}
                        >
                          {label}
                        </div>
                      </div>
                    ))}
                  </div>

                  <p className="mt-4 text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
                    In this simplified map, <span className="font-semibold text-blue-700">Gu</span>,{" "}
                    <span className="font-semibold text-blue-700">en</span>, and{" "}
                    <span className="font-semibold text-blue-700">-river</span> are
                    voiced; <span className="font-semibold text-slate-800">tt</span> and{" "}
                    <span className="font-semibold text-slate-800">sk</span> are
                    voiceless. Boundaries overlap in natural speech.
                  </p>

                  <div className="mt-4 rounded-xl border border-blue-200 bg-white px-4 py-3 text-base font-semibold leading-7 text-gray-900 sm:text-lg sm:leading-8">
                    A gap means no reliable pitch estimate—not 0 Hz.
                  </div>
                </div>
              </section>

              <details className="group rounded-2xl border border-gray-200 bg-white">
                <summary className="flex cursor-pointer list-none items-center justify-between gap-4 rounded-2xl px-5 py-4 text-lg font-semibold text-gray-900 transition hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-inset sm:text-xl [&::-webkit-details-marker]:hidden">
                  <span>When your graph looks different</span>
                  <span
                    className="text-blue-600 transition-transform group-open:rotate-180"
                    aria-hidden="true"
                  >
                   ⌄
                  </span>
                </summary>

                <div className="border-t border-gray-100 px-5 pb-5 pt-4">
                  <p className="text-base leading-7 text-gray-700 sm:text-lg sm:leading-8">
                    First listen. Then compare where the gaps occur and how long they
                    last.
                  </p>

                  <div className="mt-4 grid gap-3 md:grid-cols-2">
                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="font-semibold text-gray-900">
                        Pronunciation may differ
                      </div>
                      <ul className="mt-2 space-y-1.5 text-sm leading-6 text-gray-700 sm:text-base">
                        <li>• An added pause or breath</li>
                        <li>• A longer or shorter closure in p, t, or k</li>
                        <li>
                          • A voicing mismatch: loss of voicing, or added voicing where
                          the narrator is voiceless
                        </li>
                      </ul>
                    </div>

                    <div className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                      <div className="font-semibold text-gray-900">
                        Recording or tracking may differ
                      </div>
                      <ul className="mt-2 space-y-1.5 text-sm leading-6 text-gray-700 sm:text-base">
                        <li>• Noise or very quiet speech</li>
                        <li>• Creaky, breathy, or irregular voicing</li>
                        <li>• A brief pitch-tracking error</li>
                      </ul>
                    </div>
                  </div>

                  <p className="mt-4 text-sm leading-6 text-gray-600 sm:text-base">
                    Replay the phrase before treating a visual mismatch as a
                    pronunciation error.
                  </p>
                </div>
              </details>
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}